import { useEffect, useMemo, useState } from 'react'
import {
  Scale,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Play,
  ShieldCheck,
  Trash2,
  Ban,
  Download,
} from 'lucide-react'
import { api } from '@/lib/api'

type DsrType =
  | 'ACCESS'
  | 'RECTIFICATION'
  | 'ERASURE'
  | 'OBJECTION'
  | 'PORTABILITY'
  | 'RESTRICTION'
  | 'COMPLAINT'

type DsrStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'CANCELLED'
type Verification = 'UNVERIFIED' | 'EMAIL_MATCHED' | 'MANUAL_VERIFIED'

interface Dsr {
  id: string
  type: DsrType
  status: DsrStatus
  verification: Verification
  clientStubId: string | null
  requesterEmail: string
  requesterName: string | null
  requestBody: string
  receivedAt: string
  deadlineAt: string
  respondedAt: string | null
  resolutionNotes: string | null
  evidenceStorageKeys: string[]
  clientStub: { id: string; displayName: string; email: string | null } | null
}

type Tab = 'open' | 'closed'

function daysUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now()
  return Math.ceil(ms / (24 * 60 * 60 * 1000))
}

const TYPE_LABEL: Record<DsrType, string> = {
  ACCESS: 'Acceso (art. 15)',
  RECTIFICATION: 'Rectificación (art. 16)',
  ERASURE: 'Supresión (art. 17)',
  OBJECTION: 'Oposición (art. 21)',
  PORTABILITY: 'Portabilidad (art. 20)',
  RESTRICTION: 'Limitación (art. 18)',
  COMPLAINT: 'Queja',
}

const VERIFICATION_LABEL: Record<Verification, string> = {
  UNVERIFIED: 'Sin verificar',
  EMAIL_MATCHED: 'Email coincide',
  MANUAL_VERIFIED: 'Verificado manual',
}

const STATUS_LABEL: Record<DsrStatus, string> = {
  PENDING: 'Pendiente',
  IN_PROGRESS: 'En curso',
  COMPLETED: 'Completada',
  REJECTED: 'Rechazada',
  CANCELLED: 'Cancelada',
}

const REJECT_REASONS = [
  'Identidad no verificable tras requerimiento (art. 12.6)',
  'Petición manifiestamente infundada o excesiva (art. 12.5.b)',
  'Datos no obran en el responsable',
  'Aplica excepción art. 17.3 (libertad de expresión, obligación legal, archivo público, defensa de reclamaciones)',
  'Otra (especificar)',
]

export function RgpdQueuePage() {
  const [tab, setTab] = useState<Tab>('open')
  const [items, setItems] = useState<Dsr[]>([])
  const [busyId, setBusyId] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  async function load(signal?: AbortSignal) {
    try {
      const r = await api<{ items: Dsr[] }>(`/v1/marketing/rgpd/dsr?scope=${tab}`, { signal })
      if (signal?.aborted) return
      setItems(r.items)
    } catch (e) {
      if (signal?.aborted || (e instanceof DOMException && e.name === 'AbortError')) return
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    // AbortController evita race al cambiar de tab rápido — el fetch
    // anterior se cancela y no sobreescribe items del tab actual.
    const ac = new AbortController()
    void load(ac.signal)
    return () => ac.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  async function call(path: string, body?: unknown) {
    setErr(null)
    setInfo(null)
    return api<Record<string, unknown>>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async function verify(dsr: Dsr, method: 'EMAIL_MATCHED' | 'MANUAL_VERIFIED') {
    setBusyId(dsr.id)
    try {
      await call(`/v1/marketing/rgpd/dsr/${dsr.id}/verify`, { method })
      void load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  async function start(dsr: Dsr) {
    setBusyId(dsr.id)
    try {
      await call(`/v1/marketing/rgpd/dsr/${dsr.id}/start`)
      void load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  async function execute(dsr: Dsr) {
    if (dsr.type === 'ERASURE') {
      const titular = dsr.clientStub?.displayName ?? dsr.requesterName ?? dsr.requesterEmail
      // Doble confirmación: (1) explicación detallada con identidad clara
      // del titular, (2) requiere teclear literalmente 'BORRAR'. Reduce el
      // riesgo de clic accidental en la fila equivocada de la cola.
      const ack = confirm(
        `ESTA ACCIÓN NO ES REVERSIBLE.\n\n` +
          `Se va a seudonimizar al titular:\n  ${titular}\n  <${dsr.requesterEmail}>\n\n` +
          `Operaciones que se ejecutarán:\n` +
          `  - ClientStub: displayName='[BORRADO]', email/teléfono a null, DND global\n` +
          `  - Conversaciones: body redactado (metadata se conserva)\n` +
          `  - Consents: status WITHDRAWN\n` +
          `  - Suppression GLOBAL en todos los canales conocidos\n` +
          `  - Custom fields / tags / feedback comments: vaciados\n\n` +
          `Tras Aceptar se pedirá teclear BORRAR para confirmar.`,
      )
      if (!ack) return
      const typed = window.prompt('Para confirmar, teclea literalmente: BORRAR')
      if (typed !== 'BORRAR') {
        setInfo('Borrado cancelado: la confirmación no coincide.')
        return
      }
    }
    setBusyId(dsr.id)
    try {
      // Para OBJECTION obligamos al backend a recibir scope explícito.
      const body =
        dsr.type === 'OBJECTION' ? { scope: 'MARKETING' as const } : undefined
      const result = await call(`/v1/marketing/rgpd/dsr/${dsr.id}/execute`, body)
      if (result.ok === false && result.requiresManualHandling) {
        setInfo('Este tipo de petición requiere intervención manual del responsable.')
      } else if (result.storageKey) {
        setInfo(`Export generado: ${String(result.storageKey)}`)
      } else {
        setInfo('Ejecutado correctamente.')
      }
      void load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  async function reject(dsr: Dsr) {
    // Motivo asistido: listado de razones típicas (transparencia AEPD) +
    // texto libre cuando se elige 'Otra'. El backend exige min 10 chars.
    const list = REJECT_REASONS.map((r, i) => `${i + 1}. ${r}`).join('\n')
    const choiceStr = window.prompt(
      `Motivo del rechazo (se notificará al titular):\n\n${list}\n\nEscribe el número (1-${REJECT_REASONS.length}):`,
    )
    if (!choiceStr) return
    const choice = parseInt(choiceStr, 10)
    if (!Number.isInteger(choice) || choice < 1 || choice > REJECT_REASONS.length) {
      setErr('Selección de motivo inválida.')
      return
    }
    let reason = REJECT_REASONS[choice - 1]
    if (choice === REJECT_REASONS.length) {
      const free = window.prompt('Especifica el motivo (mínimo 10 caracteres):')
      if (!free || free.trim().length < 10) {
        setErr('El motivo debe tener al menos 10 caracteres.')
        return
      }
      reason = free.trim()
    }
    setBusyId(dsr.id)
    try {
      await call(`/v1/marketing/rgpd/dsr/${dsr.id}/reject`, { reason })
      void load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => new Date(a.deadlineAt).getTime() - new Date(b.deadlineAt).getTime()),
    [items],
  )

  return (
    <div className="p-6 max-w-5xl">
      <header className="mb-4">
        <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
          <Scale className="size-5 text-blueprint-700" />
          Cola RGPD · derechos del titular
        </h1>
        <p className="text-xs font-mono text-ink-700/60">
          RGPD art. 12.3 · plazo 1 mes · ACCESS, RECTIFICATION, ERASURE, OBJECTION,
          PORTABILITY, RESTRICTION, COMPLAINT
        </p>
      </header>

      <div className="flex gap-1 border-b border-ink-900/10 mb-4">
        {(['open', 'closed'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={[
              'px-3 py-2 text-xs font-medium border-b-2 -mb-px',
              tab === t
                ? 'border-blueprint-600 text-blueprint-700'
                : 'border-transparent text-ink-700/60 hover:text-ink-900',
            ].join(' ')}
          >
            {t === 'open' ? 'Por hacer' : 'Cerradas'}
          </button>
        ))}
      </div>

      {err && (
        <div className="text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mb-3">
          {err}
        </div>
      )}
      {info && (
        <div className="text-xs font-mono text-blueprint-700 bg-blueprint-50 border border-blueprint-200 rounded px-2 py-1 mb-3">
          {info}
        </div>
      )}

      <div className="space-y-3">
        {sortedItems.map((dsr) => {
          const days = daysUntil(dsr.deadlineAt)
          const stillOpen = dsr.status !== 'COMPLETED' && dsr.status !== 'REJECTED' && dsr.status !== 'CANCELLED'
          const urgent = stillOpen && days < 7
          const warning = stillOpen && days >= 7 && days < 15
          const erasureBlocked =
            dsr.type === 'ERASURE' && dsr.verification !== 'MANUAL_VERIFIED'
          const accessBlocked =
            dsr.type === 'ACCESS' && dsr.verification !== 'MANUAL_VERIFIED'
          return (
            <div key={dsr.id} data-testid={`dsr-card-${dsr.id}`} className="bp-card p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="bp-pill text-[10px] font-mono uppercase">{TYPE_LABEL[dsr.type]}</span>
                    <span
                      className={[
                        'text-[10px] font-mono px-1.5 py-0.5 rounded',
                        urgent
                          ? 'bg-red-100 text-red-800'
                          : warning
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-ink-900/5 text-ink-700/70',
                      ].join(' ')}
                    >
                      {urgent && <AlertTriangle className="inline size-3 mr-0.5" />}
                      {!stillOpen
                        ? STATUS_LABEL[dsr.status]
                        : days >= 0
                          ? `vence en ${days}d`
                          : `vencido ${-days}d`}
                    </span>
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded bg-ink-900/5 text-ink-700/80"
                      title={dsr.verification}
                    >
                      {VERIFICATION_LABEL[dsr.verification]}
                    </span>
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded bg-ink-900/5 text-ink-700/80"
                      title={dsr.status}
                    >
                      {STATUS_LABEL[dsr.status]}
                    </span>
                    {(erasureBlocked || accessBlocked) && stillOpen && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                        Esperando verificación manual
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 text-sm">
                    <strong>{dsr.requesterName ?? '—'}</strong>{' '}
                    <span className="text-ink-700/60">&lt;{dsr.requesterEmail}&gt;</span>
                  </div>
                  {dsr.clientStub && (
                    <div className="text-[11px] font-mono text-blueprint-700 mt-0.5">
                      → matchea con ClientStub: {dsr.clientStub.displayName}
                    </div>
                  )}
                  <p className="text-xs text-ink-700/80 mt-2 whitespace-pre-wrap line-clamp-4">
                    {dsr.requestBody}
                  </p>
                  {dsr.resolutionNotes && (
                    <details className="mt-2">
                      <summary className="text-[11px] font-mono text-ink-700/60 cursor-pointer">
                        notas de resolución
                      </summary>
                      <pre className="text-[10px] font-mono whitespace-pre-wrap text-ink-700/70 mt-1">
                        {dsr.resolutionNotes}
                      </pre>
                    </details>
                  )}
                  {dsr.evidenceStorageKeys.length > 0 && (
                    <div className="text-[10px] font-mono text-ink-700/60 mt-1.5">
                      Evidencias: {dsr.evidenceStorageKeys.join(', ')}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {dsr.status === 'PENDING' && dsr.verification === 'UNVERIFIED' && (
                    <button
                      disabled={busyId === dsr.id}
                      onClick={() => void verify(dsr, 'EMAIL_MATCHED')}
                      className="bp-btn inline-flex items-center gap-1 text-[11px]"
                    >
                      <ShieldCheck className="size-3" /> Email matched
                    </button>
                  )}
                  {dsr.status === 'PENDING' && dsr.verification !== 'MANUAL_VERIFIED' && (
                    <button
                      disabled={busyId === dsr.id}
                      onClick={() => void verify(dsr, 'MANUAL_VERIFIED')}
                      className="bp-btn inline-flex items-center gap-1 text-[11px]"
                    >
                      <ShieldCheck className="size-3" /> Verificación manual
                    </button>
                  )}
                  {dsr.status === 'PENDING' && (
                    <button
                      disabled={busyId === dsr.id}
                      onClick={() => void start(dsr)}
                      className="bp-btn inline-flex items-center gap-1 text-[11px]"
                    >
                      <Play className="size-3" /> Marcar en curso
                    </button>
                  )}
                  {(dsr.status === 'PENDING' || dsr.status === 'IN_PROGRESS') && (
                    <button
                      disabled={busyId === dsr.id || erasureBlocked || accessBlocked}
                      onClick={() => void execute(dsr)}
                      title={
                        erasureBlocked || accessBlocked
                          ? 'Requiere verificación manual antes de ejecutar'
                          : undefined
                      }
                      className="bp-btn-primary inline-flex items-center gap-1 text-[11px]"
                    >
                      {dsr.type === 'ACCESS' ? (
                        <>
                          <Download className="size-3" /> Generar export
                        </>
                      ) : dsr.type === 'ERASURE' ? (
                        <>
                          <Trash2 className="size-3" /> Ejecutar borrado
                        </>
                      ) : dsr.type === 'OBJECTION' ? (
                        <>
                          <Ban className="size-3" /> Registrar oposición
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="size-3" /> Ejecutar
                        </>
                      )}
                    </button>
                  )}
                  {(dsr.status === 'PENDING' || dsr.status === 'IN_PROGRESS') && (
                    <button
                      disabled={busyId === dsr.id}
                      onClick={() => void reject(dsr)}
                      className="bp-btn inline-flex items-center gap-1 text-[11px] text-red-700"
                    >
                      <XCircle className="size-3" /> Rechazar
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
        {sortedItems.length === 0 && (
          <div className="bp-card p-6 text-center text-xs font-mono text-ink-700/50">
            {tab === 'open' ? 'No hay peticiones abiertas.' : 'No hay peticiones cerradas.'}
          </div>
        )}
      </div>
    </div>
  )
}
